import { createRoot } from "react-dom/client";
import { ComponentType } from "react";
import "./styles/index.scss";

// Babel includes polyfills when you include these 2 files
import "core-js/stable";
import "regenerator-runtime/runtime";
import { disableReactDevTools } from "./utils/disableReactDevTools";

type AsyncComponentModule = () => Promise<{ default: ComponentType<any> }>;

if (process.env.NODE_ENV === "production") disableReactDevTools();

// Import chunks lazily to avoid loading all components at once.
// Helps when you don't use all components in a given page
// the webpackChunkName tells webpack to split that module in its own file
const components: Record<string, AsyncComponentModule> = {
  // Molecules
  SectionTitle: async () => import("@/Molecules/SectionTitle"),
  GenericModal: async () => import("@/Molecules/GenericModal"),
  SubHeader: async () => import("@/Molecules/SubHeader"),
  ActionHeader: async () => import("@/Molecules/ActionHeader"),
  Footer: async () => import("@/Molecules/Footer"),

  // Organisms
  CardGrid: async () => import("@/Organisms/CardGrid"),
  GridPage: async () => import("@/Organisms/GridPage"),
  Header: async () => import("@/Organisms/Header"),
  CrmCustomActions: async () => import("@/Organisms/CrmCustomActions"),
  CommisionCalculator: async () => import("@/Organisms/CommissionCalculator"),
  Calculator: async () => import("@/Organisms/Calculator"),
  DjangoMessage: async () => import("@/Organisms/DjangoMessage"),
  GapsedExpertFinder: async () => import("@/Organisms/GapsedExpertFinder"),
  ServerMsg: async () => import("@/Organisms/ServerMsg"),

  // Pages
  HouseEdit: async () => import("~/pages/houseEdit"),
  Calendar: async () => import("~/pages/CalendarPage"),
  GalleryEdit: async () => import("~/pages/GalleryEdit"),
  Login: async () => import("~/pages/Login"),
  Authentication: async () => import("~/pages/Authentication"),
  ResetPassword: async () => import("~/pages/ResetPassword"),
  ResetPasswordDone: async () => import("~/pages/ResetPassword/Done"),
  Dashboard: async () => import("~/pages/Dashboard"),
  TrainingDashboard: async () => import("~/pages/TrainingDashboard"),
  RetrocessionSimulator: async () => import("~/pages/RetrocessionSimulator"),
  OrderBulkImport: async () => import("~/pages/orderBulkImport"),
  FrontVisibility: async () => import("~/pages/FrontVisibilityPage"),
  FrontVisibilityEdit: async () => import("~/pages/FrontVisibilityPage/edition"),
  RecruitmentVisibility: async () => import("~/pages/RecruitmentVisibilityPage"),
  RecruitmentVisibilityEdit: async () => import("~/pages/RecruitmentVisibilityPage/edition"),

  // Forms
  FindBuyerForm: async () => import("~/forms/FindBuyerForm"),
  AddFamilyForm: async () => import("~/forms/AddFamilyForm"),
  SendMailForm: async () => import("~/forms/SendMailForm"),
  SendAdctRequestForm: async () => import("~/forms/SendAdctRequestForm"),
  GenericForm: async () => import("~/forms/GenericForm"),
  ToolEstimationFlatForm: async () => import("~/forms/flatEstimationForm"),
  ToolEstimationHouseForm: async () => import("~/forms/houseEstimationForm"),
  CreateFamilyForm: async () => import("~/forms/CreateFamilyForm"),
  CreateADVStoreOrderForm: async () => import("~/forms/CreateADVStoreOrder"),
  OptionsForm: async () => import("~/forms/OptionsForm"),

  // Contexts
  FormflowActions: async () => import("@/Contexts/FormflowActions"),
  UiLibThemeProvider: async () => import("@efficity/ui/contexts/ThemeProvider"),

  // layouts
  DetailSections: async () => import("~/layouts/DetailSections"),

  // Documents
  Documents: async () => import("~/pages/Documents"),
};

// Allows mounting components by name (from the list above)
(globalThis as any).mountComponent = async (target: HTMLElement, componentName: string, props: any) => {
  // Load a polyfill for ResizeObserver, but only when it's missing
  // Seems to be missing from iOS 13.3 safari, used by 1.1% of global users
  // 42 of which happen to be our users >_>
  if (!("ResizeObserver" in window)) {
    const { default: ResizeObserver } = await import("resize-observer-polyfill");
    (window as any).ResizeObserver = ResizeObserver;
  }

  const importFunc = components[componentName];
  const { default: Component } = await importFunc();
  const root = createRoot(target);
  root.render(<Component {...props} />);
};

// Allows unmounting components by name
(globalThis as any).unmountComponentAt = async (target: HTMLElement) => {
  const root = createRoot(target);
  root.unmount();
};

// Allows mounting components by name (from the list above)
(globalThis as any).mountWrappedComponent = async (
  target: HTMLElement,
  wrapperName: string,
  componentName: string,
  props: any,
) => {
  // Load a polyfill for ResizeObserver, but only when it's missing
  // Seems to be missing from iOS 13.3 safari, used by 1.1% of global users
  // 42 of which happen to be our users >_>
  if (!("ResizeObserver" in window)) {
    const { default: ResizeObserver } = await import("resize-observer-polyfill");
    (window as any).ResizeObserver = ResizeObserver;
  }

  const componentImportFunc = components[componentName];
  const wrapperImportFunc = components[wrapperName];
  const [{ default: Component }, { default: Wrapper }] = await Promise.all([
    componentImportFunc(),
    wrapperImportFunc(),
  ]);

  const root = createRoot(target);

  root.render(
    <Wrapper>
      <Component {...props} />
    </Wrapper>,
  );
};

// Allows unmounting components by name
(globalThis as any).unmountComponentAt = async (target: HTMLElement) => {
  const root = createRoot(target);
  root.unmount();
};
